import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, makeStyles, TextField, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EntrancePosition } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from '../components/MapEditorCanvas';
import SectionTitle from '../components/SectionTitle';
import {
    areaBBoxPx,
    computeRoom,
    EditorArea,
    POLYGON_PRESETS,
    Point,
    SizeMeters,
    snapMeters,
    snapPointMeters,
    TABLE_SHAPES,
    TableShape,
} from '../mapGeometry';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const MAGENTA_BORDER = 'rgba(153, 86, 126, 0.3)';

const MAP_SIZE_MIN = 1;
const MAP_SIZE_MAX = 100;
const AREA_BBOX_MIN = 0.5;
// Matches the backend's MapTableSizeOutOfRange bounds (TablesMapManager): a dimension
// must fit both the absolute cap and the map's own size on that axis.
const TABLE_SIZE_MIN = 0.2;
const TABLE_SIZE_MAX = 10;
const CORNER_HANDLE_RADIUS = 6;

const TABLE_SHAPE_LABEL_KEY: Record<TableShape, string> = {
    round: 'tableMaps.wizard.roundTable',
    square: 'tableMaps.wizard.squareTable',
    rectangle: 'tableMaps.wizard.rectangleTable',
};

type AreaPreset = 'rectangle' | 'square' | 'circle' | 'oval' | 'L' | 'T' | 'U';
const AREA_PRESETS: AreaPreset[] = ['rectangle', 'square', 'circle', 'oval', 'L', 'T', 'U'];
const PRESET_LABEL_KEY: Record<AreaPreset, string> = {
    rectangle: 'tableMaps.wizard.areaShapeRectangle',
    square: 'tableMaps.wizard.areaShapeSquare',
    circle: 'tableMaps.wizard.areaShapeCircle',
    oval: 'tableMaps.wizard.areaShapeOval',
    L: 'tableMaps.wizard.areaShapeL',
    T: 'tableMaps.wizard.areaShapeT',
    U: 'tableMaps.wizard.areaShapeU',
};

function applyPreset(preset: AreaPreset, area: EditorArea): Partial<EditorArea> {
    switch (preset) {
        case 'rectangle':
            return { kind: 'Rectangle', points: null };
        case 'square': {
            const side = Math.min(area.size.width, area.size.height);
            return { kind: 'Rectangle', points: null, size: { width: side, height: side } };
        }
        case 'circle': {
            const side = Math.min(area.size.width, area.size.height);
            return { kind: 'Ellipse', points: null, size: { width: side, height: side } };
        }
        case 'oval':
            return { kind: 'Ellipse', points: null };
        case 'L':
        case 'T':
        case 'U':
            return { kind: 'Polygon', points: POLYGON_PRESETS[preset] };
    }
}

function pointsEqual(a: Point[] | null, b: Point[]): boolean {
    if (!a || a.length !== b.length) return false;
    return a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
}

/** Which preset (if any) the current area matches — used to highlight the active button. */
function detectPreset(area: EditorArea): AreaPreset | null {
    if (area.kind === 'Polygon') {
        if (pointsEqual(area.points, POLYGON_PRESETS.L)) return 'L';
        if (pointsEqual(area.points, POLYGON_PRESETS.T)) return 'T';
        if (pointsEqual(area.points, POLYGON_PRESETS.U)) return 'U';
        return null;
    }
    const isSquareLike = area.size.width === area.size.height;
    if (area.kind === 'Rectangle') return isSquareLike ? 'square' : 'rectangle';
    return isSquareLike ? 'circle' : 'oval';
}

function clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
}

/** Local text-input state + committed-ref sync, matching the rotation field's pattern. */
function useNumericField(
    committed: number,
    isValid: (n: number) => boolean,
    onCommit: (n: number) => void
) {
    const [input, setInput] = useState(String(committed));
    const lastCommitted = useRef(committed);

    useEffect(() => {
        if (committed !== lastCommitted.current) {
            setInput(String(committed));
            lastCommitted.current = committed;
        }
    }, [committed]);

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value;
        setInput(raw);
        const n = Number(raw);
        if (raw.trim() === '' || Number.isNaN(n) || !isValid(n)) return;
        const rounded = Math.round(n * 100) / 100;
        lastCommitted.current = rounded;
        onCommit(rounded);
    }

    return { input, onChange };
}

function clientToSVG(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

const useStyles = makeStyles({
    root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    infoBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: MAGENTA_LIGHT,
        border: `1px solid ${MAGENTA_BORDER}`,
    },
    infoBannerIcon: {
        color: MAGENTA,
        fontSize: 20,
        flexShrink: 0,
    },
    infoBannerText: {
        fontSize: 13,
        color: MAGENTA,
        fontWeight: 500,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    gridLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
    },
    gridToggleButton: {
        minWidth: 48,
        height: 32,
        padding: '0 12px',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey['300']}`,
        cursor: 'pointer',
        letterSpacing: 0.5,
    },
    gridToggleOn: {
        backgroundColor: MAGENTA,
        color: 'white',
        borderColor: MAGENTA,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    gridToggleOff: {
        backgroundColor: 'white',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    markEntranceButton: {
        height: 32,
        padding: '0 14px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    markEntranceActive: {
        backgroundColor: MAGENTA,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    markEntranceInactive: {
        backgroundColor: 'white',
        color: MAGENTA,
        border: `1px solid ${MAGENTA}`,
        '&:hover': {
            backgroundColor: MAGENTA_LIGHT,
        },
    },
    removeEntranceButton: {
        background: 'none',
        border: 'none',
        padding: '0 4px',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        color: MAGENTA,
        letterSpacing: 0.3,
        textDecoration: 'underline',
        '&:hover': {
            color: theme.palette.primary.dark,
        },
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    settingsPanel: {
        width: 272,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        paddingRight: 24,
        boxSizing: 'border-box' as const,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
    },
    fieldRow: {
        display: 'flex',
        gap: 12,
    },
    defaultSizeList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    defaultSizeLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: 4,
    },
    presetGrid: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,
    },
    presetButton: {
        minWidth: 0,
        flex: '1 1 30%',
        height: 32,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey['300']}`,
    },
    presetButtonActive: {
        backgroundColor: MAGENTA,
        color: 'white',
        borderColor: MAGENTA,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    presetButtonInactive: {
        backgroundColor: 'white',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    rightSpacer: {
        width: 272,
        flexShrink: 0,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        textAlign: 'center' as const,
        minHeight: 18,
        paddingLeft: 272,
        paddingRight: 272,
    },
});

interface DefaultTableSizeRowProps {
    shape: TableShape;
    size: SizeMeters;
    maxSize: SizeMeters;
    onChange: (size: SizeMeters) => void;
}

function DefaultTableSizeRow({ shape, size, maxSize, onChange }: DefaultTableSizeRowProps) {
    const classes = useStyles();
    const widthField = useNumericField(
        size.width,
        (n) => n >= TABLE_SIZE_MIN && n <= maxSize.width,
        (width) => onChange({ width, height: size.height })
    );
    const heightField = useNumericField(
        size.height,
        (n) => n >= TABLE_SIZE_MIN && n <= maxSize.height,
        (height) => onChange({ width: size.width, height })
    );

    return (
        <div>
            <Typography className={classes.defaultSizeLabel}>
                {i18n.t(TABLE_SHAPE_LABEL_KEY[shape])}
            </Typography>
            <div className={classes.fieldRow}>
                <TextField
                    label={i18n.t('tableMaps.wizard.tableWidth')}
                    type="number"
                    value={widthField.input}
                    onChange={widthField.onChange}
                    variant="filled"
                    fullWidth
                    inputProps={{ min: TABLE_SIZE_MIN, max: maxSize.width, step: 0.01 }}
                />
                <TextField
                    label={i18n.t('tableMaps.wizard.tableLength')}
                    type="number"
                    value={heightField.input}
                    onChange={heightField.onChange}
                    variant="filled"
                    fullWidth
                    inputProps={{ min: TABLE_SIZE_MIN, max: maxSize.height, step: 0.01 }}
                />
            </div>
        </div>
    );
}

export default function MapConfigurationStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const svgRef = useRef<SVGSVGElement>(null);
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);

    const [zoom, setZoom] = useState(1.0);
    const [isMarkingEntrance, setIsMarkingEntrance] = useState(false);

    function handleGridToggle() {
        dispatch({ type: 'SET_GRID_ENABLED', payload: !draft.gridEnabled });
    }

    function handleMarkEntranceToggle() {
        setIsMarkingEntrance((prev) => !prev);
    }

    function handleRemoveEntrance() {
        dispatch({ type: 'SET_ENTRANCE', payload: null });
        setIsMarkingEntrance(false);
    }

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    const handleEntranceChange = useCallback(
        (position: EntrancePosition) => {
            dispatch({ type: 'SET_ENTRANCE', payload: position });
            setIsMarkingEntrance(false);
        },
        [dispatch]
    );

    const mapWidthField = useNumericField(
        draft.size.width,
        (n) => n >= MAP_SIZE_MIN && n <= MAP_SIZE_MAX,
        (width) => dispatch({ type: 'SET_SIZE', payload: { width, height: draft.size.height } })
    );
    const mapHeightField = useNumericField(
        draft.size.height,
        (n) => n >= MAP_SIZE_MIN && n <= MAP_SIZE_MAX,
        (height) => dispatch({ type: 'SET_SIZE', payload: { width: draft.size.width, height } })
    );

    const areaWidthField = useNumericField(
        draft.area.size.width,
        (n) => n >= AREA_BBOX_MIN,
        (width) =>
            dispatch({
                type: 'SET_AREA',
                payload: { size: { width, height: draft.area.size.height } },
            })
    );
    const areaHeightField = useNumericField(
        draft.area.size.height,
        (n) => n >= AREA_BBOX_MIN,
        (height) =>
            dispatch({
                type: 'SET_AREA',
                payload: { size: { width: draft.area.size.width, height } },
            })
    );
    const areaOffsetXField = useNumericField(
        draft.area.offset.x,
        (n) => n >= 0,
        (x) => dispatch({ type: 'SET_AREA', payload: { offset: { x, y: draft.area.offset.y } } })
    );
    const areaOffsetYField = useNumericField(
        draft.area.offset.y,
        (n) => n >= 0,
        (y) => dispatch({ type: 'SET_AREA', payload: { offset: { x: draft.area.offset.x, y } } })
    );

    function handlePresetSelect(preset: AreaPreset) {
        dispatch({ type: 'SET_AREA', payload: applyPreset(preset, draft.area) });
    }

    const activePreset = detectPreset(draft.area);

    const maxTableSize: SizeMeters = {
        width: Math.min(TABLE_SIZE_MAX, draft.size.width),
        height: Math.min(TABLE_SIZE_MAX, draft.size.height),
    };

    // Corner resize: drag one corner of the area bbox, opposite corner stays fixed.
    const dragCornerAnchor = useRef<Point | null>(null);

    const handleCornerDown = useCallback(
        (corner: number, e: React.PointerEvent<SVGCircleElement>) => {
            e.stopPropagation();
            e.currentTarget.setPointerCapture(e.pointerId);
            const { offset, size } = draft.area;
            const corners: Point[] = [
                { x: offset.x, y: offset.y },
                { x: offset.x + size.width, y: offset.y },
                { x: offset.x + size.width, y: offset.y + size.height },
                { x: offset.x, y: offset.y + size.height },
            ];
            dragCornerAnchor.current = corners[(corner + 2) % 4];
        },
        [draft.area]
    );

    const handleCornerMove = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            const anchor = dragCornerAnchor.current;
            if (!anchor || !svgRef.current) return;
            const svgPt = clientToSVG(svgRef.current, e.clientX, e.clientY);
            const corner: Point = {
                x: clamp((svgPt.x - room.x) / room.pxPerMeter, 0, draft.size.width),
                y: clamp((svgPt.y - room.y) / room.pxPerMeter, 0, draft.size.height),
            };
            dispatch({
                type: 'SET_AREA',
                payload: {
                    offset: snapPointMeters({
                        x: Math.min(anchor.x, corner.x),
                        y: Math.min(anchor.y, corner.y),
                    }),
                    size: {
                        width: snapMeters(Math.abs(corner.x - anchor.x)),
                        height: snapMeters(Math.abs(corner.y - anchor.y)),
                    },
                },
            });
        },
        [room, draft.size, dispatch]
    );

    const handleCornerUp = useCallback(() => {
        dragCornerAnchor.current = null;
    }, []);

    const bboxPx = areaBBoxPx(draft.area, room);
    const cornersPx: Point[] = [
        { x: bboxPx.x, y: bboxPx.y },
        { x: bboxPx.x + bboxPx.width, y: bboxPx.y },
        { x: bboxPx.x + bboxPx.width, y: bboxPx.y + bboxPx.height },
        { x: bboxPx.x, y: bboxPx.y + bboxPx.height },
    ];

    const zoomPercent = Math.round(zoom * 100);
    const entranceExists = draft.entrance !== null;

    return (
        <div className={classes.root}>
            <SectionTitle>{i18n.t('tableMaps.wizard.mapConfiguration')}</SectionTitle>

            {isMarkingEntrance && (
                <div className={classes.infoBanner}>
                    <InfoOutlinedIcon className={classes.infoBannerIcon} />
                    <Typography className={classes.infoBannerText}>
                        {i18n.t('tableMaps.wizard.selectWallHint')}
                    </Typography>
                </div>
            )}

            <div className={classes.body}>
                <div className={classes.settingsPanel}>
                    <div>
                        <Typography className={classes.panelHeading}>
                            {i18n.t('tableMaps.wizard.mapSize')}
                        </Typography>
                        <div className={classes.fieldRow}>
                            <TextField
                                label={i18n.t('tableMaps.wizard.mapWidth')}
                                type="number"
                                value={mapWidthField.input}
                                onChange={mapWidthField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: MAP_SIZE_MIN, max: MAP_SIZE_MAX, step: 0.01 }}
                            />
                            <TextField
                                label={i18n.t('tableMaps.wizard.mapLength')}
                                type="number"
                                value={mapHeightField.input}
                                onChange={mapHeightField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: MAP_SIZE_MIN, max: MAP_SIZE_MAX, step: 0.01 }}
                            />
                        </div>
                    </div>

                    <div>
                        <Typography className={classes.panelHeading}>
                            {i18n.t('tableMaps.wizard.areaShape')}
                        </Typography>
                        <div className={classes.presetGrid}>
                            {AREA_PRESETS.map((preset) => (
                                <Button
                                    key={preset}
                                    className={`${classes.presetButton} ${
                                        activePreset === preset
                                            ? classes.presetButtonActive
                                            : classes.presetButtonInactive
                                    }`}
                                    onClick={() => handlePresetSelect(preset)}
                                >
                                    {i18n.t(PRESET_LABEL_KEY[preset])}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Typography className={classes.panelHeading}>
                            {i18n.t('tableMaps.wizard.areaSize')}
                        </Typography>
                        <div className={classes.fieldRow}>
                            <TextField
                                label={i18n.t('tableMaps.wizard.areaWidth')}
                                type="number"
                                value={areaWidthField.input}
                                onChange={areaWidthField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: AREA_BBOX_MIN, step: 0.01 }}
                            />
                            <TextField
                                label={i18n.t('tableMaps.wizard.areaLength')}
                                type="number"
                                value={areaHeightField.input}
                                onChange={areaHeightField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: AREA_BBOX_MIN, step: 0.01 }}
                            />
                        </div>
                        <div className={classes.fieldRow} style={{ marginTop: 12 }}>
                            <TextField
                                label={i18n.t('tableMaps.wizard.areaOffsetX')}
                                type="number"
                                value={areaOffsetXField.input}
                                onChange={areaOffsetXField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                                label={i18n.t('tableMaps.wizard.areaOffsetY')}
                                type="number"
                                value={areaOffsetYField.input}
                                onChange={areaOffsetYField.onChange}
                                variant="filled"
                                fullWidth
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </div>
                    </div>

                    <div>
                        <Typography className={classes.panelHeading}>
                            {i18n.t('tableMaps.wizard.defaultTableSizes')}
                        </Typography>
                        <div className={classes.defaultSizeList}>
                            {TABLE_SHAPES.map((shape) => (
                                <DefaultTableSizeRow
                                    key={shape}
                                    shape={shape}
                                    size={draft.tableDefaults[shape]}
                                    maxSize={maxTableSize}
                                    onChange={(size) =>
                                        dispatch({
                                            type: 'SET_TABLE_DEFAULT_SIZE',
                                            payload: { shape, size },
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.toolbarLeft}>
                            <Typography className={classes.gridLabel}>
                                {i18n.t('tableMaps.wizard.grid')}
                            </Typography>
                            <button
                                className={`${classes.gridToggleButton} ${
                                    draft.gridEnabled ? classes.gridToggleOn : classes.gridToggleOff
                                }`}
                                onClick={handleGridToggle}
                                type="button"
                            >
                                {draft.gridEnabled
                                    ? i18n.t('tableMaps.wizard.on')
                                    : i18n.t('tableMaps.wizard.off')}
                            </button>
                        </div>

                        <div className={classes.toolbarRight}>
                            {entranceExists && (
                                <button
                                    className={classes.removeEntranceButton}
                                    onClick={handleRemoveEntrance}
                                    type="button"
                                >
                                    {i18n.t('tableMaps.wizard.removeEntrance')}
                                </button>
                            )}

                            <Button
                                variant={isMarkingEntrance ? 'contained' : 'outlined'}
                                className={`${classes.markEntranceButton} ${
                                    isMarkingEntrance
                                        ? classes.markEntranceActive
                                        : classes.markEntranceInactive
                                }`}
                                onClick={handleMarkEntranceToggle}
                                startIcon={<MeetingRoomIcon style={{ fontSize: 16 }} />}
                                disabled={entranceExists}
                            >
                                {i18n.t('tableMaps.wizard.markEntrance')}
                            </Button>

                            <div className={classes.zoomControls}>
                                <button
                                    className={classes.zoomButton}
                                    onClick={handleZoomOut}
                                    disabled={zoom <= ZOOM_MIN}
                                    type="button"
                                    aria-label="Zoom out"
                                >
                                    −
                                </button>
                                <span className={classes.zoomLabel}>{zoomPercent}%</span>
                                <button
                                    className={classes.zoomButton}
                                    onClick={handleZoomIn}
                                    disabled={zoom >= ZOOM_MAX}
                                    type="button"
                                    aria-label="Zoom in"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={classes.canvasWrapper}>
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            size={draft.size}
                            area={draft.area}
                            entrance={draft.entrance}
                            isMarkingEntrance={isMarkingEntrance}
                            onEntranceChange={handleEntranceChange}
                            allowEntranceDrag
                            showGlobalRectGuide
                            svgRef={svgRef}
                        >
                            {cornersPx.map((c, i) => (
                                <circle
                                    key={i}
                                    cx={c.x}
                                    cy={c.y}
                                    r={CORNER_HANDLE_RADIUS}
                                    fill="white"
                                    stroke={MAGENTA}
                                    strokeWidth={2}
                                    style={{ cursor: 'nwse-resize' }}
                                    onPointerDown={(e) => handleCornerDown(i, e)}
                                    onPointerMove={handleCornerMove}
                                    onPointerUp={handleCornerUp}
                                />
                            ))}
                        </MapEditorCanvas>
                    </div>
                </div>

                <div className={classes.rightSpacer} />
            </div>

            <Typography className={classes.hintText}>
                {entranceExists ? i18n.t('tableMaps.wizard.dragEntranceHint') : ''}
            </Typography>
        </div>
    );
}
